import MapboxDraw from '@mapbox/mapbox-gl-draw'
import { useControl } from 'react-map-gl'
import { useEffect, useMemo } from 'react'
import type { MapRef, ControlPosition } from 'react-map-gl'

const noop = () => {}

type DrawControlProps = ConstructorParameters<typeof MapboxDraw>[0] & {
  position?: ControlPosition
  initGeometry?: GeoJSON.FeatureCollection
  initFeatures: (features: { [key: string]: GeoJSON.Feature }) => void
  onCreate?: (evt: { features: object[] }) => void
  onUpdate?: (evt: { features: object[]; action: string }) => void
  onDelete?: (evt: { features: object[] }) => void
  drawRef?: React.MutableRefObject<MapboxDraw | null>
  onDrawReady?: (draw: MapboxDraw) => void
}

export default function DrawControl(props: DrawControlProps) {
  const { initGeometry, initFeatures, onCreate, onUpdate, onDelete, position, drawRef, onDrawReady, ...drawOptions } =
    props
  const draw = useMemo(() => new MapboxDraw(drawOptions), [])
  const handleCreate = onCreate ?? noop
  const handleUpdate = onUpdate ?? noop
  const handleDelete = onDelete ?? noop

  useEffect(() => {
    if (drawRef) {
      drawRef.current = draw
    }
    onDrawReady?.(draw)
    return () => {
      if (drawRef) {
        drawRef.current = null
      }
    }
  }, [draw, drawRef, onDrawReady])

  useControl<MapboxDraw>(
    () => draw,
    ({ map }: { map: MapRef }) => {
      if (initGeometry) {
        // Adding features one-by-one triggers a render per feature in mapbox-gl-draw.
        // Adding the full FeatureCollection renders once and is significantly faster for multipolygons.
        draw.add(initGeometry)
        const nextFeatures: { [key: string]: GeoJSON.Feature } = {}
        for (const feature of draw.getAll()?.features ?? []) {
          const featureId = (feature as any)?.id
          if (!featureId) continue
          nextFeatures[String(featureId)] = feature as GeoJSON.Feature
        }
        initFeatures(nextFeatures)
      }
      map.on('draw.create', handleCreate)
      map.on('draw.update', handleUpdate)
      map.on('draw.delete', handleDelete)
    },
    ({ map }: { map: MapRef }) => {
      map.off('draw.create', handleCreate)
      map.off('draw.update', handleUpdate)
      map.off('draw.delete', handleDelete)
    },
    {
      position,
    },
  )

  return null
}

DrawControl.defaultProps = {
  onCreate: () => {},
  onUpdate: () => {},
  onDelete: () => {},
  initGeometry: undefined,
  initFeatures: () => {},
}
